/**
 * Pure sectioning for the Notifications widget: what the user must do (NOW),
 * what is coming up (NEXT, shown as "Later"), and what already happened (LOG,
 * collapsed into "updates happened in the background"). Priority is a separate
 * axis — see priorityTier.ts — and so is how long a notice is shown at all —
 * see lifecycle.ts.
 */

export type NotificationTier = "now" | "next" | "log";

export type TierableNotification = {
  id?: unknown;
  _key?: unknown;
  summary?: unknown;
  source?: unknown;
  notificationType?: unknown;
  sourceObjectType?: unknown;
  presentationMode?: unknown;
  urgency?: unknown;
  actionRequired?: unknown;
  categories?: unknown;
  extraFields?: unknown;
  bundleType?: unknown;
  topPriorityEvent?: unknown;
};

export type NotificationTierResult = {
  tier: NotificationTier;
  /** Best-effort start time of the thing the notice is about (ms epoch). */
  anchorAt: number | null;
};

/** A NEXT item stays upcoming until its anchor is this far in the past. */
export const NEXT_GRACE_MS = 15 * 60 * 1000;
/** A notice about a moment this far in the past is history: it never holds NOW again. */
export const MOMENT_PASSED_MS = 60 * 60 * 1000;

const NOW_TYPES = new Set([
  "conflict_alert",
  "deadline_alert",
  "reply_attention",
  "integration_alert",
  "reauthorize_required",
  "integration_review",
  "overload_suggestion",
  "obligation_follow_up",
  "weather_alert",
  // The week's "Done for you" digest: what standing mandates did on their
  // own, seen once each time a new run (or one that did not go through) lands.
  "mandate_digest",
]);

const NEXT_TYPES = new Set([
  "meeting_brief",
  "reminder",
  "derived_prep",
  "proactive_reminder",
  "calendar_derived_reminders",
]);

// Receipts: never promoted to NEXT by an incidental start time in their payload.
const LOG_TYPES = new Set([
  "digest",
  "sync_summary",
  "meeting_confirmed",
  "task_update",
  "shared_activity",
  "networking_suggestion",
  "follow_up_tracker",
]);

// Follow-ups come after a moment (a meeting's action items, a promised reply), so
// a past meeting time never ends them, even when they are also typed as a brief.
export const FOLLOW_UP_TYPES = new Set([
  "follow_up",
  "follow_up_pending",
  "follow_up_tracker",
  "obligation_follow_up",
  "counterparty_obligation",
]);

// Event start keys first: a prep reminder's own startTime is its lead time.
export const ANCHOR_KEYS = [
  "meetingStartTime",
  "eventStartTime",
  "sourceEventStartTime",
  "relatedEventStartTime",
  "startTime",
  // Deadline alerts (proactive insights spread their metadata into extraFields).
  "deadlineTimestamp",
] as const;

const text = (value: unknown) => (typeof value === "string" ? value.trim().toLowerCase() : "");
const record = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
export const notificationTime = (value: unknown): number | null => {
  const numeric = typeof value === "string" && value.trim() && !Number.isNaN(Number(value)) ? Number(value) : value;
  if (typeof numeric === "number") return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
  if (typeof numeric === "string") {
    const parsed = Date.parse(numeric);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

/** Every kind word a notice carries: its type, its source, and its categories. */
export const notificationKinds = (item: TierableNotification) => {
  const categories = Array.isArray(item.categories) ? item.categories.map(text) : [];
  return new Set([text(item.notificationType), text(item.source), ...categories].filter(Boolean));
};

export function getNotificationAnchor(item: TierableNotification): number | null {
  const extra = record(item.extraFields);
  const sources = [extra, record(item), record(item.topPriorityEvent), record(extra?.topPriorityEvent)];
  for (const source of sources) {
    if (!source) continue;
    for (const key of ANCHOR_KEYS) {
      const anchor = notificationTime(source[key]);
      if (anchor) return anchor;
    }
  }
  // Stored rows pack `events` into extraFields; rows read back carry it on top.
  const events = [extra?.events, record(item)?.events].find(Array.isArray) ?? [];
  return notificationTime(record(events[0])?.startTime);
}

const isPlatformActionMessage = (item: TierableNotification) => {
  const communication = record(record(item.extraFields)?.platformCommunication);
  const action = record(communication?.action);
  return (
    (text(item.notificationType) === "platform_message" || text(item.source) === "platform_communication") &&
    Boolean(text(action?.url) && text(action?.label))
  );
};

// A message waiting on the user's reply holds NOW until dismissed, read or not.
const isReplyNeededMessage = (item: TierableNotification, itemKinds: Set<string>) =>
  itemKinds.has("message_attention") ||
  (text(item.sourceObjectType) === "message" && item.presentationMode === "moment_alert");

const isAccessRequest = (item: TierableNotification) =>
  text(item.sourceObjectType) === "platform_access_request" &&
  String(item.id ?? item._key ?? "").startsWith("access:");

/** Reminders and briefs: notices about a point in time that is still ahead or just passed. */
export function isTimeAnchoredNotification(item: TierableNotification): boolean {
  const itemKinds = Array.from(notificationKinds(item));
  if (itemKinds.some((kind) => FOLLOW_UP_TYPES.has(kind))) return false;
  return (
    itemKinds.some((kind) => NEXT_TYPES.has(kind)) ||
    item.bundleType === "pre_meeting_context" ||
    record(item.extraFields)?.bundleType === "pre_meeting_context"
  );
}

/** Something the user must still act on: an explicit action, a reply, an access request, a platform action. */
export function needsUserAction(item: TierableNotification): boolean {
  return (
    item.actionRequired === true ||
    isReplyNeededMessage(item, notificationKinds(item)) ||
    isAccessRequest(item) ||
    isPlatformActionMessage(item)
  );
}

export function classifyNotificationTier(
  item: TierableNotification,
  { isRead, now }: { isRead: boolean; now: number },
): NotificationTierResult {
  const anchorAt = getNotificationAnchor(item);
  const urgency = typeof item.urgency === "number" ? item.urgency : Number(item.urgency ?? 2) || 2;
  const itemKinds = notificationKinds(item);
  const has = (set: Set<string>) => Array.from(itemKinds).some((kind) => set.has(kind));
  const nowResult: NotificationTierResult = { tier: "now", anchorAt };
  const logResult: NotificationTierResult = { tier: "log", anchorAt };
  const isTimeAnchored = isTimeAnchoredNotification(item);
  const momentPassed = anchorAt !== null && anchorAt < now - MOMENT_PASSED_MS;

  // "Starts in 30 minutes" for a meeting that ended hours ago asks nothing of the user.
  if (isTimeAnchored && momentPassed) return logResult;

  // Something the user must still act on holds NOW until it is resolved or dismissed.
  if (needsUserAction(item)) return nowResult;
  // An attention kind with nothing to act on holds NOW only until it has been seen.
  const isAttentionKind = has(NOW_TYPES) || (itemKinds.has("device_health") && urgency >= 3);
  if (isAttentionKind && !isRead) return nowResult;

  // NEXT kinds win over receipt kinds: the feed defaults a missing source to sync_summary.
  const isReceipt = !isTimeAnchored && has(LOG_TYPES);
  const isUpcoming = anchorAt !== null && anchorAt >= now - NEXT_GRACE_MS;
  if (isUpcoming && (isTimeAnchored || (!isReceipt && anchorAt > now))) return { tier: "next", anchorAt };

  // Just past its anchor, an unread alert still holds NOW until the user has seen it.
  if (!isRead && !momentPassed && (urgency >= 4 || item.presentationMode === "moment_alert")) return nowResult;
  return logResult;
}
